<script>
    import ContentBlock from '@itslearning/prometheus/assets/templates/ContentBlock/v1/ContentBlock.svelte';
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte';
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import Modal from '@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte';

    import FormField from '../FormField/FormField.svelte';

    import { tick } from 'svelte';
    import { guidV4 } from '@itslearning/atlas/generate/guid';
    import { synchronizerToken, i18n } from '../../stores';
    import { fieldIds, externalCourseEnrollmentErrorType } from '../../constants';
    import { fromFieldSettingsController } from '../../formFieldSettingsController';
    import { submitPersonData } from '../../api';

    export let customerId = -1;
    export let languageId = -1;
    export let courseId = -1;
    export let formFields = [];
    export let customFields = {};

    const firstNameInputId = `firstName-${guidV4()}`;
    const lastNameInputId = `lastName-${guidV4()}`;
    const userNameInputId = `userName-${guidV4()}`;
    const passwordInputId = `password-${guidV4()}`;
    const addressLine1InputId = `addressLine1-${guidV4()}`;
    const addressLine2InputId = `addressLine2-${guidV4()}`;
    const postalCodeInputId = `postalCode-${guidV4()}`;
    const postalAddressInputId = `postalAddress-${guidV4()}`;
    const emailAddressInputId = `emailAddress-${guidV4()}`;
    const phoneInputId = `phone-${guidV4()}`;
    const cellularPhoneInputId = `cellularPhone-${guidV4()}`;
    const customField1InputId = `customField1-${guidV4()}`;
    const customField2InputId = `customField2-${guidV4()}`;
    const customField3InputId = `customField3-${guidV4()}`;
    const customField4InputId = `customField4-${guidV4()}`;
    const customField5InputId = `customField5-${guidV4()}`;
    
    let errorBannerContainer = null;
    let isErrorMessageVisible = false;
    let errorMessage = null;
    let errorMessageHtml = null;
    let firstName = null;
    let lastName = null;
    let userName = null;
    let isUserNameValid = true;
    let userNameErrorMessage = null;
    let password = null;
    let isPasswordValid = true;
    let passwordErrorMessage = null;
    let retypePassword = null;
    let addressLine1 = null;
    let addressLine2 = null;
    let postalCode = null;
    let postalAddress = null;
    let emailAddress = null;
    let isEmailAddressValid = true;
    let emailAddressErrorMessage = null;
    let phone = null;
    let cellularPhone = null;
    let customField1 = null;
    let customField2 = null;
    let customField3 = null;
    let customField4 = null;
    let customField5 = null;
    let isValidationDialogVisible = false;
    let validationDialogHeading = null;
    let validationDialogBody = null;
    let fieldWithError = null;
    let isEnrollmentRequestSent = false;
    let enrollmentResultMessage = null;

    $: fieldsSettings = formFields && formFields.length > 0
        ? new Map([
            [ fieldIds.firstName, getSettingsForField(fieldIds.firstName, formFields) ],
            [ fieldIds.lastName, getSettingsForField(fieldIds.lastName, formFields) ],
            [ fieldIds.userName, getSettingsForField(fieldIds.userName, formFields) ],
            [ fieldIds.password, getSettingsForField(fieldIds.password, formFields) ],
            [ fieldIds.address1, getSettingsForField(fieldIds.address1, formFields) ],
            [ fieldIds.address2, getSettingsForField(fieldIds.address2, formFields) ],
            [ fieldIds.postalCode, getSettingsForField(fieldIds.postalCode, formFields) ],
            [ fieldIds.postalAddress, getSettingsForField(fieldIds.postalAddress, formFields) ],
            [ fieldIds.emailAddress, getSettingsForField(fieldIds.emailAddress, formFields) ],
            [ fieldIds.phone, getSettingsForField(fieldIds.phone, formFields) ],
            [ fieldIds.cellularPhone, getSettingsForField(fieldIds.cellularPhone, formFields) ],
            [ fieldIds.customString1, getSettingsForField(fieldIds.customString1, formFields) ],
            [ fieldIds.customString2, getSettingsForField(fieldIds.customString2, formFields) ],
            [ fieldIds.customString3, getSettingsForField(fieldIds.customString3, formFields) ],
            [ fieldIds.customString4, getSettingsForField(fieldIds.customString4, formFields) ],
            [ fieldIds.customString5, getSettingsForField(fieldIds.customString5, formFields) ]])
        : new Map();

    $: settingsController = fromFieldSettingsController(fieldsSettings);

    $: hasAnyCustomField = customFields && (
        customFields.field1Title ||
        customFields.field2Title ||
        customFields.field3Title ||
        customFields.field4Title ||
        customFields.field5Title);

    function closeErrorBanner() {
        isErrorMessageVisible = false;
        errorMessage = null;
        errorMessageHtml = null;
    }

    function showErrorBanner(error) {
        if (error.isHtml) {
            errorMessageHtml = error.text;
        } else {
            errorMessage = error.text;
        }

        isErrorMessageVisible = true;
        errorBannerContainer.scrollIntoView();
    }

    async function closeValidationDialog() {
        isValidationDialogVisible = false;
        validationDialogHeading = null;
        validationDialogBody = null;
        await tick();

        if (fieldWithError) {
            fieldWithError.focus();
            fieldWithError = null;
        }
    }

    function clearPasswordValidationError() {
        isPasswordValid = true;
        passwordErrorMessage = null;
    }

    function clearUserNameValidationError() {
        isUserNameValid = true;
        userNameErrorMessage = true;
    }

    function clearEmailAddressValidationError() {
        isEmailAddressValid = true;
        emailAddressErrorMessage = null;
    }

    async function submit() {
        closeErrorBanner();
        clearPasswordValidationError();
        clearUserNameValidationError();
        clearEmailAddressValidationError();

        const firstNameSettings = fieldsSettings.get(fieldIds.firstName);

        if (firstNameSettings.isVisible && firstNameSettings.isRequired && !firstName) {
            isValidationDialogVisible = true;
            validationDialogHeading = $i18n.first_name;
            validationDialogBody = $i18n.this_field_is_mandatory;
            fieldWithError = getInputByTextFieldId(firstNameInputId);

            return;
        }

        const lastNameSettings = fieldsSettings.get(fieldIds.lastName);

        if (lastNameSettings.isVisible && lastNameSettings.isRequired && !lastName) {
            isValidationDialogVisible = true;
            validationDialogHeading = $i18n.last_name;
            validationDialogBody = $i18n.this_field_is_mandatory;
            fieldWithError = getInputByTextFieldId(lastNameInputId);

            return;
        }

        const userNameSettings = fieldsSettings.get(fieldIds.userName);

        if (userNameSettings.isVisible && userNameSettings.isEditable) {
            if (userNameSettings.isRequired && !userName) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.username;
                validationDialogBody = $i18n.this_field_is_mandatory;
                fieldWithError = getInputByTextFieldId(userNameInputId);

                return;
            }

            if (userName && userName.includes('  ')) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.username;

                const errorMessage = $i18n.admin_Add_person_Username_double_spaces_error;

                validationDialogBody = errorMessage;
                fieldWithError = getInputByTextFieldId(userNameInputId);
                
                isUserNameValid = false;
                userNameErrorMessage = errorMessage;

                return;
            }
        }

        const passwordSettings = fieldsSettings.get(fieldIds.password);

        if (passwordSettings.isVisible && passwordSettings.isEditable) {
            if (passwordSettings.isRequired && !password && !retypePassword) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.password;
                validationDialogBody = $i18n.this_field_is_mandatory;
                fieldWithError = getInputByTextFieldId(passwordInputId, 'password');
                isPasswordValid = false;

                return;
            }

            if (password !== retypePassword) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.password;

                const errorMessage = $i18n.password_confirm_error;

                validationDialogBody = errorMessage;
                fieldWithError = getInputByTextFieldId(passwordInputId, 'password');

                isPasswordValid = false;
                passwordErrorMessage = errorMessage;

                return;
            }   
        }

        const address1Settings = fieldsSettings.get(fieldIds.address1);

        if (address1Settings.isVisible && address1Settings.isRequired && !addressLine1) {
            isValidationDialogVisible = true;
            validationDialogHeading = $i18n.address_line_1;
            validationDialogBody = $i18n.this_field_is_mandatory;
            fieldWithError = getInputByTextFieldId(addressLine1InputId);

            return;
        }

        const address2Settings = fieldsSettings.get(fieldIds.address1);

        if (address2Settings.isVisible && address2Settings.isRequired && !addressLine2) {
            isValidationDialogVisible = true;
            validationDialogHeading = $i18n.address_line_2;
            validationDialogBody = $i18n.this_field_is_mandatory;
            fieldWithError = getInputByTextFieldId(addressLine2InputId);

            return;
        }

        const postalCodeSettings = fieldsSettings.get(fieldIds.postalCode);
        const postalAddressSettings = fieldsSettings.get(fieldIds.postalAddress);

        const isPostalInfoVisible = postalCodeSettings.isVisible || postalAddressSettings.isVisible;
        const isPostalInfoRequired = postalCodeSettings.isRequired || postalAddressSettings.isRequired;

        if (isPostalInfoVisible && isPostalInfoRequired) {
            if (!postalCode) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.postal_code;
                validationDialogBody = $i18n.this_field_is_mandatory;
                fieldWithError = getInputByTextFieldId(postalCodeInputId);

                return;
            }

            if (!postalAddress) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.postal_address;
                validationDialogBody = $i18n.this_field_is_mandatory;
                fieldWithError = getInputByTextFieldId(postalAddressInputId);

                return;
            }
        }

        const emailAddressSettings = fieldsSettings.get(fieldIds.emailAddress);

        if (emailAddressSettings.isVisible && emailAddressSettings.isEditable) {
            if (emailAddressSettings.isRequired && !emailAddress) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.email_address;
                validationDialogBody = $i18n.this_field_is_mandatory;
                fieldWithError = getInputByTextFieldId(emailAddressInputId);

                return;
            }

            if (emailAddressSettings && !isValidEmail(emailAddress)) {
                isValidationDialogVisible = true;
                validationDialogHeading = $i18n.email_address;

                const errorMessage = $i18n.email_address_invalid;

                validationDialogBody = errorMessage;
                fieldWithError = getInputByTextFieldId(emailAddressInputId);
                
                isEmailAddressValid = false;
                emailAddressErrorMessage = errorMessage;

                return;
            }
        }

        const phoneSettings = fieldsSettings.get(fieldIds.phone);

        if (phoneSettings.isVisible && phoneSettings.isRequired && !phone) {
            isValidationDialogVisible = true;
            validationDialogHeading = $i18n.phone;
            validationDialogBody = $i18n.this_field_is_mandatory;
            fieldWithError = getInputByTextFieldId(phoneInputId);

            return;
        }

        const cellularPhoneSettings = fieldsSettings.get(fieldIds.cellularPhone);

        if (cellularPhoneSettings.isVisible && cellularPhoneSettings.isRequired && !cellularPhone) {
            isValidationDialogVisible = true;
            validationDialogHeading = $i18n.person_Cellular_phone;
            validationDialogBody = $i18n.this_field_is_mandatory;
            fieldWithError = getInputByTextFieldId(cellularPhoneInputId);

            return;
        }

        if (hasAnyCustomField) {
            if (customFields.field1Title) {
                const customString1Settings = fieldsSettings.get(fieldIds.customString1);

                if (customString1Settings.isVisible && customString1Settings.isRequired && !customField1) {
                    isValidationDialogVisible = true;
                    validationDialogHeading = customFields.field1Title;
                    validationDialogBody = $i18n.this_field_is_mandatory;
                    fieldWithError = getInputByTextFieldId(customField1InputId);

                    return;
                }
            }

            if (customFields.field2Title) {
                const customString2Settings = fieldsSettings.get(fieldIds.customString2);

                if (customString2Settings.isVisible && customString2Settings.isRequired && !customField2) {
                    isValidationDialogVisible = true;
                    validationDialogHeading = customFields.field2Title;
                    validationDialogBody = $i18n.this_field_is_mandatory;
                    fieldWithError = getInputByTextFieldId(customField2InputId);

                    return;
                }
            }

            if (customFields.field3Title) {
                const customString3Settings = fieldsSettings.get(fieldIds.customString3);

                if (customString3Settings.isVisible && customString3Settings.isRequired && !customField3) {
                    isValidationDialogVisible = true;
                    validationDialogHeading = customFields.field3Title;
                    validationDialogBody = $i18n.this_field_is_mandatory;
                    fieldWithError = getInputByTextFieldId(customField3InputId);

                    return;
                }
            }

            if (customFields.field4Title) {
                const customString4Settings = fieldsSettings.get(fieldIds.customString4);

                if (customString4Settings.isVisible && customString4Settings.isRequired && !customField4) {
                    isValidationDialogVisible = true;
                    validationDialogHeading = customFields.field4Title;
                    validationDialogBody = $i18n.this_field_is_mandatory;
                    fieldWithError = getInputByTextFieldId(customField4InputId);

                    return;
                }
            }

            if (customFields.field5Title) {
                const customString5Settings = fieldsSettings.get(fieldIds.customString5);

                if (customString5Settings.isVisible && customString5Settings.isRequired && !customField5) {
                    isValidationDialogVisible = true;
                    validationDialogHeading = customFields.field5Title;
                    validationDialogBody = $i18n.this_field_is_mandatory;
                    fieldWithError = getInputByTextFieldId(customField5InputId);

                    return;
                }
            }
        }
        
        const enrollment = {
            firstName,
            lastName,
            addressLine1,
            addressLine2,
            postalCode,
            postalAddress,
            emailAddress,
            phone,
            cellularPhone,
            customField1,
            customField2,
            customField3,
            customField4,
            customField5
        };

        const requestModel = {
            enrollment,
            synchronizerToken: $synchronizerToken
        };

        try {
            await submitPersonData(courseId, customerId, languageId, requestModel);

            enrollmentResultMessage = $i18n.course_enrollment_external_Application_sent_message;
            isEnrollmentRequestSent = true;
        }
        catch (error) {
            await processApiError(error);
        }
    }

    function getErrorMessage(error) {
        let text = $i18n.error_Unknown;
        let isHtml = false;

        switch (error.paramValue.errorType) {
            case externalCourseEnrollmentErrorType.invalidProfile:
                text = $i18n.course_enrollment_external_Profile_is_not_valid;
                isHtml = true;
                break;
            case externalCourseEnrollmentErrorType.invalidEmail:
                text = $i18n.email_address_invalid;
                break;
            default:
                text = $i18n.error_Unknown;
                break;
        }

        return { text, isHtml };
    }

    async function processApiError(apiError) {
        try {
            showErrorBanner(getErrorMessage(await apiError.response.json()));
        }
        catch(error) {
            showErrorBanner({
                text: $i18n.error_Unknown,
                isHtml: false
            });
        }
    }

    const getSettingsForField = (fieldId, fields) => {
        const fieldSettings = fields.find(f => f.fieldId === fieldId);

        return fieldSettings !== null && fieldSettings !== undefined ? { isVisible: true, ...fieldSettings } : { isVisible: false };
    };

    const getInputByTextFieldId = (textFieldId, fieldType = 'text') => document.querySelector(`input[id*='${textFieldId}'][type='${fieldType}']`);

    const isValidEmail = email => !email || /^[^()<>@,;:\\".[\] \000-\037\177*&]+(\.[^()<>@,;:\\".[\] \000-\037\177*&]+)*@[^()<>@,;:\\".[\] \000-\037\177*&]+(\.[^()<>@,;:\\".[\] \000-\037\177*&]+)*$/i.test(email);
</script>

<svelte:head>
    <title>{$i18n.registration_form}</title>
</svelte:head>


<ContentBlock title={$i18n.registration_form}
    headingLevel="h1"
    contentAreaLayout="narrow">

    {#if isEnrollmentRequestSent}
        <Banner
            visible={isEnrollmentRequestSent}
            text={enrollmentResultMessage}
            closable={false}
        />
    {:else}
        <div bind:this={errorBannerContainer}>
            <Banner
                visible={isErrorMessageVisible}
                text={errorMessage}
                html={errorMessageHtml}
                type="warning"
                close={$i18n.close}
                on:hidden={closeErrorBanner}
            />
        </div>
        <FormField
            id={firstNameInputId}
            label={$i18n.first_name}
            bind:value={firstName}
            autocomplete="given-name"
            fieldId={fieldIds.firstName}
            settings={fieldsSettings}
        />
        <FormField
            id={lastNameInputId}
            label={$i18n.last_name}
            autocomplete="family-name"
            bind:value={lastName}
            fieldId={fieldIds.lastName}
            settings={fieldsSettings}
        />
        {#if settingsController.isFieldVisible(fieldIds.userName)}
            <FormField
                id={userNameInputId}
                label={$i18n.username}
                bind:value={userName}
                autocomplete="username"
                fieldId={fieldIds.userName}
                settings={fieldsSettings}
                errorMessage={userNameErrorMessage}
                hasError={!isUserNameValid}/>
        {/if}
        {#if settingsController.isFieldVisible(fieldIds.password)}
            <FormField
                id={passwordInputId}
                label={$i18n.password}
                bind:value={password}
                autocomplete="new-password"
                type="password"
                fieldId={fieldIds.password}
                settings={fieldsSettings}
                errorMessage={passwordErrorMessage}
                hasError={!isPasswordValid}
            />
            <FormField
                id={`retypePassword-${guidV4()}`}
                label={$i18n.retype_password}
                bind:value={retypePassword}
                autocomplete="new-password"
                type="password"
                fieldId={fieldIds.password}
                settings={fieldsSettings}
                errorMessage={passwordErrorMessage}
                hasError={!isPasswordValid}
            />      
        {/if}
        
        <FormField
            id={addressLine1InputId}
            label={$i18n.address_line_1}
            bind:value={addressLine1}
            autocomplete="address-line1"
            fieldId={fieldIds.address1}
            settings={fieldsSettings}
        />
        <FormField
            id={addressLine2InputId}
            label={$i18n.address_line_2}
            bind:value={addressLine2}
            autocomplete="address-line2"
            fieldId={fieldIds.address2}
            settings={fieldsSettings}
        />
        <FormField
            id={postalCodeInputId}
            label={$i18n.postal_code}
            bind:value={postalCode}
            autocomplete="postal-code"
            fieldId={fieldIds.postalCode}
            settings={fieldsSettings}
        />
        <FormField
            id={postalAddressInputId}
            label={$i18n.postal_address}
            bind:value={postalAddress}
            autocomplete="on"
            fieldId={fieldIds.postalAddress}
            settings={fieldsSettings}
        />
        <FormField
            id={emailAddressInputId}
            label={$i18n.email_address}
            bind:value={emailAddress}
            autocomplete="email"
            fieldId={fieldIds.emailAddress}
            settings={fieldsSettings}
            errorMessage={emailAddressErrorMessage}
            hasError={!isEmailAddressValid}
        />
        <FormField
            id={phoneInputId}
            label={$i18n.phone}
            bind:value={phone}
            autocomplete="tel"
            fieldId={fieldIds.phone}
            settings={fieldsSettings}
        />
        <FormField
            id={cellularPhoneInputId}
            label={$i18n.person_Cellular_phone}
            bind:value={cellularPhone}
            autocomplete="tel"
            fieldId={fieldIds.cellularPhone}
            settings={fieldsSettings}
        />
        {#if hasAnyCustomField}
            {#if customFields.field1Title}
                <FormField
                    id={customField1InputId}
                    label={customFields.field1Title}
                    bind:value={customField1}
                    autocomplete="on"
                    fieldId={fieldIds.customString1}
                    settings={fieldsSettings}
                />
            {/if}
            {#if customFields.field2Title}
                <FormField
                    id={customField2InputId}
                    label={customFields.field2Title}
                    bind:value={customField2}
                    autocomplete="on"
                    fieldId={fieldIds.customString2}
                    settings={fieldsSettings}
                />
            {/if}
            {#if customFields.field3Title}
                <FormField
                    id={customField3InputId}
                    label={customFields.field3Title}
                    bind:value={customField3}
                    autocomplete="on"
                    fieldId={fieldIds.customString3}
                    settings={fieldsSettings}
                />
            {/if}
            {#if customFields.field4Title}
                <FormField
                    id={customField4InputId}
                    label={customFields.field4Title}
                    bind:value={customField4}
                    autocomplete="on"
                    fieldId={fieldIds.customString4}
                    settings={fieldsSettings}
                />
            {/if}
            {#if customFields.field5Title}
                <FormField
                    id={customField5InputId}
                    label={customFields.field5Title}
                    bind:value={customField5}
                    autocomplete="on"
                    fieldId={fieldIds.customString5}
                    settings={fieldsSettings}
                />
            {/if}
        {/if}
        <div
            aria-hidden="true"
            class="itsl-course-enrollment-person-data-submission__asterisk-hint">
            <span class="itsl-course-enrollment-person-data-submission__asterisk">*</span>{$i18n.mandatory}
        </div>
        <div class="itsl-course-enrollment-person-data-submission__buttons">
            <Button
                type="primary"
                htmlType="button"
                text={$i18n.ok}
                on:click={submit}
            />
            <Modal
                enabled={isValidationDialogVisible}
                titleText={validationDialogHeading}
                bodyText={validationDialogBody}
                confirmText={$i18n.ok}
                showCancel={false}
                on:close={closeValidationDialog}
            />
        </div>
    {/if}

</ContentBlock>

